/**
 * Page share dialog: toggle per user organization.
 * A shared page (and its entire subtree + blocks + attachments) becomes
 * synced with all members of the organization.
 */
import { For, Show } from "solid-js";
import { Loader2, Users } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { useI18n } from "@/providers/i18n-provider";
import { usePageSharing } from "../hooks/use-page-sharing";

interface PageShareDialogProps {
  pageId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PageShareDialog(props: PageShareDialogProps) {
  const { t } = useI18n();
  const sharing = usePageSharing(() => props.pageId, { enabled: () => props.open });

  const handleToggle = async (organizationId: string) => {
    try {
      await sharing.toggleShare(organizationId);
    } catch (error) {
      console.error("Error toggling page share:", error);
      showToast(t(msg`Could not update sharing. Please try again.`), "error");
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Users class="h-5 w-5" />
          </div>
          <DialogTitle class="text-xl font-bold">{t(msg`Share page`)}</DialogTitle>
        </div>
      </DialogHeader>

      <div class="space-y-4 p-6">
        <DialogDescription class="text-sm text-muted-foreground">
          {t(
            msg`Members of the selected organizations will be able to view and edit this page, its subpages and attachments.`,
          )}
        </DialogDescription>

        {/* P23 / CC-60 · I3 — mentira con consecuencias en una app de documentos:
            con `listOrganizations` caído el diálogo decía «You don't belong to
            any organization yet», invitando a crear una organización que el
            usuario YA tiene. Y con `getPageOrganizations` caído, los
            interruptores salían todos apagados: «este documento no está
            compartido» sin saberlo. */}
        <Show when={sharing.organizations.isError || sharing.sharedOrgIds.isError}>
          <QueryErrorState
            testId="page-share-error"
            compact
            title={t(msg`Couldn't load your sharing settings`)}
            description={t(
              msg`We couldn't reach the server. This does NOT mean this page is unshared.`,
            )}
            onRetry={() => {
              void sharing.organizations.refetch();
              void sharing.sharedOrgIds.refetch();
            }}
          />
        </Show>
        <Show when={!sharing.organizations.isError && !sharing.sharedOrgIds.isError}>
          <Show
            when={!sharing.organizations.isLoading}
            fallback={
              <div class="flex justify-center py-6">
                <Loader2 class="h-5 w-5 animate-spin text-muted-foreground/50" />
              </div>
            }
          >
            <Show
              when={(sharing.organizations.data?.length ?? 0) > 0}
              fallback={
                <p data-testid="page-share-empty" class="py-4 text-sm text-muted-foreground/70">
                  {t(msg`You don't belong to any organization yet. Create one to share pages.`)}
                </p>
              }
            >
              <ul class="space-y-2">
                <For each={sharing.organizations.data ?? []}>
                  {(org) => (
                    <li class="flex items-center justify-between gap-3 rounded-xl border border-border/40 px-4 py-3">
                      <span class="truncate text-sm font-medium">{org.name}</span>
                      <Switch
                        checked={sharing.isShared(org.id)}
                        disabled={sharing.isToggling() !== null}
                        onCheckedChange={() => {
                          void handleToggle(org.id);
                        }}
                        aria-label={t(msg`Share with this organization`)}
                      />
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </Show>
        </Show>
      </div>
    </Dialog>
  );
}
